import { ElementType, useMemo } from "react";

import { useTranslation } from "next-i18next";

import merge from "deepmerge";
import { TOptions } from "i18next";
import MarkdownToJSX, {
  type MarkdownToJSX as MarkdownNamespace,
} from "markdown-to-jsx";

import { Heading } from "@/ui/cva/Heading";

/*
 * Static on purpose. This used to be `next/dynamic` to keep markdown-to-jsx's
 * parser out of the shared _app chunk, but a lazy component is still loading
 * on the first client render whenever its initializer misses Next's
 * `preloadReady` pass. The server HTML had the text, the client rendered the
 * empty loading state, and React threw #418 on every PDP (the brand promise
 * USPs) and dropped the whole root to a client render (#423).
 *
 * Markdown that only shows after an interaction (a modal, a tooltip) and sits
 * in the app shell takes `LazyLokaliseMarkdownText` instead, which keeps the
 * parser out of _app without touching server-rendered HTML.
 */

type MarkdownProps = {
  className?: string;
  wrapper?: ElementType;
  overrides?: MarkdownNamespace.Overrides;
  children: string;
};

const Markdown = ({
  className,
  wrapper,
  overrides,
  children,
}: MarkdownProps) => {
  return (
    <MarkdownToJSX
      className={className}
      options={{
        wrapper,
        forceWrapper: true,
        overrides: merge<MarkdownNamespace.Overrides>(
          {
            h1: {
              component: Heading,
              props: {
                as: "span",
                size: "h1",
              },
            },
            h2: {
              component: Heading,
              props: {
                as: "span",
                size: "h2",
              },
            },
            h3: {
              component: Heading,
              props: {
                as: "span",
                size: "h3",
              },
            },
            h4: {
              component: Heading,
              props: {
                as: "span",
                size: "h4",
              },
            },
            h5: {
              component: Heading,
              props: {
                as: "span",
                size: "h5",
              },
            },
            h6: {
              component: Heading,
              props: {
                as: "span",
                size: "h6",
              },
            },
            ol: {
              props: {
                className: "list-decimal pl-5",
              },
            },
            ul: {
              props: {
                className: "list-disc pl-5",
              },
            },
            a: {
              props: {
                className: "text-brand-500 hover:underline",
              },
            },
            p: {
              props: {
                className: "whitespace-pre-wrap",
              },
            },
            em: {
              props: {
                className: "not-italic text-brand-500",
              },
            },
          },
          overrides ?? {}
        ),
      }}
    >
      {children}
    </MarkdownToJSX>
  );
};

export const MarkdownText = ({ wrapper = "div", ...props }: MarkdownProps) => {
  return <Markdown {...props} wrapper={wrapper} />;
};

export type LokaliseMarkdownTextProps = Omit<MarkdownProps, "children"> & {
  lokaliseKey: string;
  options?: TOptions;
};

export const LokaliseMarkdownText = ({
  lokaliseKey,
  options,
  wrapper = "div",
  ...props
}: LokaliseMarkdownTextProps) => {
  const { t } = useTranslation();

  const markdownContent = useMemo(() => {
    /**
     * Multiline text from Lokalise is downloaded as single entry inside a json (namespace) file.
     * This entry contains \\n string for each newline and because of that we are replacing all
     * occurrences of \\n string with \n (newline character)
     */
    return t(lokaliseKey, options).replaceAll("\\n", "\n");
  }, [lokaliseKey, options, t]);

  return (
    <Markdown {...props} wrapper={wrapper}>
      {markdownContent}
    </Markdown>
  );
};

export default LokaliseMarkdownText;
